import { Link } from 'react-router-dom'
import { Check } from 'lucide-react'
import type { Contact } from '@/api/people'
import { useListAmountFormat } from '@/hooks/useFormatCurrency'
import { ROUTES } from '@/routes/routes'
import { cn } from '@/lib/utils'
import { PersonAvatar } from './PersonAvatar'
import { formatActivity, netOf } from './peopleStats'
import { PEOPLE_TABLE_COLUMNS } from './peopleUi'

export function PersonRow({ contact: c }: { contact: Contact }) {
  const format = useListAmountFormat()
  const net = netOf(c)
  const both = c.owedToUs > 0 && c.weOwe > 0
  const activity = c.lastActivityOn ? `Last activity ${formatActivity(c.lastActivityOn)}` : null
  const tone = net > 0 ? 'text-[var(--fl-income)]' : 'text-[var(--fl-expense)]'

  return (
    <li className="border-b border-[var(--fl-divider)] last:border-b-0">
      <Link
        to={ROUTES.APP.PERSON_DETAIL(c.id)}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--fl-surface-hover)] md:items-center md:px-5 md:py-3',
          PEOPLE_TABLE_COLUMNS,
          c.archived && 'opacity-70'
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-3">
          <PersonAvatar name={c.name} net={net} />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-[var(--fl-heading)] md:text-base">
              {c.name}
            </span>
            <span className="block truncate text-[11.5px] text-[var(--fl-text-3)] md:hidden">
              {both
                ? `${format(c.owedToUs)} to you · ${format(c.weOwe)} you owe`
                : (activity ?? (net === 0 ? 'Nothing yet' : ''))}
            </span>
          </span>
        </span>

        <span className="hidden text-[13px] text-[var(--fl-text-2)] md:block">
          {c.lastActivityOn ? formatActivity(c.lastActivityOn) : '—'}
        </span>
        <span className="hidden text-right text-sm tabular-nums text-[var(--fl-text-1)] md:block">
          {c.owedToUs > 0 ? format(c.owedToUs) : <span className="text-[var(--fl-text-3)]">—</span>}
        </span>
        <span className="hidden text-right text-sm tabular-nums text-[var(--fl-text-1)] md:block">
          {c.weOwe > 0 ? format(c.weOwe) : <span className="text-[var(--fl-text-3)]">—</span>}
        </span>

        {net === 0 ? (
          <span className="flex shrink-0 items-center justify-end gap-1 justify-self-end rounded-full bg-[var(--fl-surface-subtle)] px-2.5 py-1 text-xs text-[var(--fl-text-2)] md:bg-transparent md:px-0">
            <Check size={12} strokeWidth={2.5} aria-hidden="true" />
            Settled
          </span>
        ) : (
          <span className={cn('shrink-0 text-right', tone)}>
            <span className="block text-[11px] md:inline md:text-xs">
              {net > 0 ? 'owes you' : 'you owe'}
            </span>{' '}
            <span className="block font-serif text-[15px] font-semibold tabular-nums md:inline md:text-base">
              {format(Math.abs(net))}
            </span>
          </span>
        )}
      </Link>
    </li>
  )
}
